import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { 
  Camera, Copy, Check, Instagram, Hash, MapPin, AtSign,
  Clock, Zap, ChevronDown, ChevronUp, Sparkles, Share2,
  ExternalLink, Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { trackPostEvent, POST_EVENTS, startPostSession } from '../utils/postAnalytics';
import PostConfirmationPrompt from './PostConfirmationPrompt';

/**
 * PostActivationKit - Quick post tools for talent after completing activations
 * 
 * Features:
 * - Pre-written caption templates
 * - Suggested hashtags (one-tap copy)
 * - Brand tag suggestions
 * - Best time to post indicator
 * - Platform-specific tips
 */

// Platform posting windows (all times in local user timezone)
const POSTING_WINDOWS = {
  instagram: {
    name: 'Instagram',
    icon: Instagram,
    color: 'pink',
    bestHours: [9, 10, 11, 18, 19, 20], // 9-11 AM, 6-8 PM
    bestDays: [2, 3, 4], // Tue, Wed, Thu
  },
  tiktok: {
    name: 'TikTok',
    icon: () => <span className="text-sm font-bold">TT</span>,
    color: 'cyan',
    bestHours: [11, 12, 13, 19, 20, 21, 22], // 11 AM-1 PM, 7-10 PM
    bestDays: [2, 3, 5], // Tue, Wed, Fri
  },
  linkedin: {
    name: 'LinkedIn',
    icon: () => <span className="text-sm font-bold">in</span>,
    color: 'blue',
    bestHours: [8, 9, 10], // 8-10 AM
    bestDays: [2, 3, 4], // Tue, Wed, Thu
  },
  twitter: {
    name: 'X',
    icon: () => <span className="text-sm font-bold">X</span>,
    color: 'slate',
    bestHours: [8, 9, 10, 11], // 8-11 AM
    bestDays: [1, 2, 3, 4, 5], // Mon-Fri
  }
};

// Hashtag categories
const HASHTAG_SETS = {
  activation: ['#brandactivation', '#experientialmarketing', '#eventmarketing', '#brandambassador', '#liveevent'],
  professional: ['#eventstaff', '#eventprofessional', '#hospitalitylife', '#giglife', '#eventlife'],
  engagement: ['#eventday', '#behindthescenes', '#onsite', '#eventcrew', '#activation'],
  location: ['#miami', '#losangeles', '#nyc', '#chicago', '#eventsinla', '#eventsinmiami']
};

const PostTimingIndicator = ({ platform = 'instagram' }) => {
  const [status, setStatus] = useState({ level: 'off-peak', message: '' });
  
  useEffect(() => {
    const checkTiming = () => {
      const now = new Date();
      const hour = now.getHours();
      const day = now.getDay();
      const config = POSTING_WINDOWS[platform];
      
      const isGoodHour = config.bestHours.includes(hour);
      const isGoodDay = config.bestDays.includes(day);
      
      if (isGoodHour && isGoodDay) {
        setStatus({ level: 'best', message: 'Best time to post right now!' });
      } else if (isGoodHour || isGoodDay) {
        setStatus({ level: 'good', message: 'Good time to post' });
      } else {
        // Find next best window
        const nextBestHour = config.bestHours.find(h => h > hour) || config.bestHours[0];
        const hoursUntil = nextBestHour > hour ? nextBestHour - hour : (24 - hour) + nextBestHour;
        setStatus({ level: 'off-peak', message: `Best window in ${hoursUntil}h` });
      }
    };
    
    checkTiming();
    const interval = setInterval(checkTiming, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [platform]);
  
  const colors = {
    best: 'bg-green-100 text-green-700 border-green-200',
    good: 'bg-amber-100 text-amber-700 border-amber-200',
    'off-peak': 'bg-gray-100 text-gray-600 border-gray-200'
  };
  
  const icons = {
    best: '🟢',
    good: '🟡',
    'off-peak': '⚪'
  };
  
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${colors[status.level]}`}>
      <span>{icons[status.level]}</span>
      <span className="text-sm font-medium">{status.message}</span>
    </div>
  );
};

const HashtagButton = ({ tag, onCopy }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = () => {
    navigator.clipboard.writeText(tag);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  
  return (
    <button
      onClick={handleCopy}
      className={`px-2 py-1 text-xs rounded-full transition-all ${
        copied 
          ? 'bg-green-100 text-green-700 border-green-300' 
          : 'bg-cyan-50 text-cyan-700 border-cyan-200 hover:bg-cyan-100'
      } border`}
    >
      {copied ? <Check className="w-3 h-3 inline mr-1" /> : null}
      {tag}
    </button>
  );
};

const PostActivationKit = ({ 
  recentBooking = null, // { venue_name, brand_name, location, event_type }
  onUploadPhotos,
  compact = false 
}) => {
  const [selectedPlatform, setSelectedPlatform] = useState('instagram');
  const [caption, setCaption] = useState('');
  const [expanded, setExpanded] = useState(!compact);
  const [copiedCaption, setCopiedCaption] = useState(false);
  const [copiedHashtags, setCopiedHashtags] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [hasUsedPostingAction, setHasUsedPostingAction] = useState(false);
  
  // Generate caption based on recent booking
  useEffect(() => {
    if (recentBooking) {
      generateCaption(recentBooking);
      // Start a post session for tracking
      startPostSession(recentBooking.booking_id, recentBooking.venue_name);
      trackPostEvent(POST_EVENTS.POST_KIT_OPENED, { venue: recentBooking.venue_name });
    } else {
      setCaption(getDefaultCaption());
    }
  }, [recentBooking]);
  
  // Show confirmation immediately after Copy Caption OR Share action
  useEffect(() => {
    if (hasUsedPostingAction) {
      // Small delay to not interrupt user
      const timer = setTimeout(() => {
        setShowConfirmation(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [hasUsedPostingAction]);
  
  const getDefaultCaption = () => {
    return `Another amazing activation in the books! 🎯

Great energy, amazing team, and unforgettable moments.

Looking forward to the next one! 💪

#brandactivation #eventlife`;
  };
  
  const generateCaption = (booking) => {
    const templates = [
      `Just wrapped up an incredible activation at ${booking.venue_name || 'this amazing venue'}! 🎯\n\nThe energy was unmatched and the team was amazing. Grateful for another successful event!\n\n${booking.brand_name ? `@${booking.brand_name.toLowerCase().replace(/\s+/g, '')}` : ''}\n\n#brandactivation #eventlife`,
      
      `Behind the scenes at today's activation! 📸\n\n${booking.venue_name ? `📍 ${booking.venue_name}` : ''}\n\nAnother successful event with an incredible team. This is what we do! 💪\n\n#experientialmarketing #eventcrew`,
      
      `Event day energy hits different! ⚡\n\n${booking.venue_name ? `Today at ${booking.venue_name}` : 'Another activation'} - connecting brands with real people.\n\nLove what I do! 🙌\n\n#brandambassador #eventmarketing`
    ];
    
    setCaption(templates[Math.floor(Math.random() * templates.length)]);
  };
  
  const handleCopyCaption = () => {
    navigator.clipboard.writeText(caption);
    setCopiedCaption(true);
    trackPostEvent(POST_EVENTS.COPY_CAPTION, { platform: selectedPlatform });
    setHasUsedPostingAction(true); // Trigger confirmation
    toast.success('Caption copied!');
    setTimeout(() => setCopiedCaption(false), 2000);
  };
  
  const handleCopyAllHashtags = () => {
    const allTags = [...HASHTAG_SETS.activation, ...HASHTAG_SETS.professional.slice(0, 3)].join(' ');
    navigator.clipboard.writeText(allTags);
    setCopiedHashtags(true);
    trackPostEvent(POST_EVENTS.COPY_ALL_HASHTAGS, { platform: selectedPlatform });
    toast.success('Hashtags copied!');
    setTimeout(() => setCopiedHashtags(false), 2000);
  };
  
  const platformConfig = POSTING_WINDOWS[selectedPlatform];
  
  if (compact && !expanded) {
    return (
      <Card className="border-pink-200 bg-gradient-to-br from-pink-50/50 to-white">
        <CardContent className="p-3">
          <button 
            onClick={() => setExpanded(true)}
            className="w-full flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-pink-500" />
              <span className="font-medium text-gray-900 text-sm">Post Your Activation</span>
              <PostTimingIndicator platform={selectedPlatform} />
            </div>
            <ChevronDown className="w-4 h-4 text-gray-400" />
          </button>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card className="border-pink-200 bg-gradient-to-br from-pink-50/30 to-white" data-testid="post-activation-kit">
      <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-pink-500" />
          Post Your Activation
        </CardTitle>
        {compact && (
          <button onClick={() => setExpanded(false)}>
            <ChevronUp className="w-4 h-4 text-gray-400" />
          </button>
        )}
      </CardHeader>
      
      <CardContent className="pt-0 px-4 pb-4 space-y-4">
        {/* Timing Indicator */}
        <PostTimingIndicator platform={selectedPlatform} />
        
        {/* Platform Selector */}
        <div className="flex gap-2">
          {Object.entries(POSTING_WINDOWS).map(([key, config]) => {
            const Icon = config.icon;
            return (
              <button
                key={key}
                onClick={() => setSelectedPlatform(key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                  selectedPlatform === key
                    ? `bg-${config.color}-100 text-${config.color}-700 border-${config.color}-300 border`
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {typeof Icon === 'function' ? <Icon /> : <Icon className="w-3 h-3" />}
                {config.name}
              </button>
            );
          })}
        </div>
        
        {/* Pre-Check Checklist */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-xs font-medium text-amber-800 mb-2">Before you post:</p>
          <div className="space-y-1.5">
            <label className="flex items-center gap-2 text-xs text-amber-700 cursor-pointer">
              <input type="checkbox" className="rounded border-amber-300" />
              <AtSign className="w-3 h-3" /> Tag the brand
            </label>
            <label className="flex items-center gap-2 text-xs text-amber-700 cursor-pointer">
              <input type="checkbox" className="rounded border-amber-300" />
              <MapPin className="w-3 h-3" /> Include location
            </label>
            <label className="flex items-center gap-2 text-xs text-amber-700 cursor-pointer">
              <input type="checkbox" className="rounded border-amber-300" />
              <Hash className="w-3 h-3" /> Use activation hashtags
            </label>
          </div>
        </div>
        
        {/* Caption Generator */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-gray-700">Caption</label>
            <Button
              size="sm"
              variant="ghost"
              className="h-6 text-xs text-pink-600 hover:text-pink-700"
              onClick={() => generateCaption(recentBooking || {})}
            >
              <Sparkles className="w-3 h-3 mr-1" />
              Regenerate
            </Button>
          </div>
          <Textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            className="text-sm min-h-[100px] border-pink-200 focus:border-pink-400"
            placeholder="Your activation caption..."
          />
          <Button
            size="sm"
            className={`w-full mt-2 ${copiedCaption ? 'bg-green-500' : 'bg-pink-500 hover:bg-pink-600'}`}
            onClick={handleCopyCaption}
          >
            {copiedCaption ? (
              <>
                <Check className="w-4 h-4 mr-2" />
                Copied!
              </>
            ) : (
              <>
                <Copy className="w-4 h-4 mr-2" />
                Copy Caption
              </>
            )}
          </Button>
        </div>
        
        {/* Hashtags */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-xs font-medium text-gray-700">Hashtags</label>
            <Button
              size="sm"
              variant="outline"
              className={`h-6 text-xs ${copiedHashtags ? 'border-green-300 text-green-600' : ''}`}
              onClick={handleCopyAllHashtags}
            >
              {copiedHashtags ? <Check className="w-3 h-3 mr-1" /> : <Copy className="w-3 h-3 mr-1" />}
              Copy All
            </Button>
          </div>
          
          <div className="space-y-2">
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">Activation</p>
              <div className="flex flex-wrap gap-1">
                {HASHTAG_SETS.activation.map(tag => (
                  <HashtagButton key={tag} tag={tag} />
                ))}
              </div>
            </div>
            <div>
              <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">Professional</p>
              <div className="flex flex-wrap gap-1">
                {HASHTAG_SETS.professional.map(tag => (
                  <HashtagButton key={tag} tag={tag} />
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Upload Photos CTA */}
        {onUploadPhotos && (
          <Button
            variant="outline"
            className="w-full border-pink-200 hover:bg-pink-50 text-pink-700"
            onClick={onUploadPhotos}
          >
            <Camera className="w-4 h-4 mr-2" />
            Upload Event Photos
          </Button>
        )}
        
        {/* Tip */}
        <div className="text-center text-[11px] text-gray-500 bg-gray-50 rounded-lg p-2">
          💡 Tip: Post within 2 hours of your event for maximum engagement
        </div>
      </CardContent>
      
      {/* Post Confirmation Prompt */}
      <PostConfirmationPrompt
        isOpen={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        venueName={recentBooking?.venue_name || 'your activation'}
      />
    </Card>
  );
};

export { PostActivationKit, PostTimingIndicator, HASHTAG_SETS, POSTING_WINDOWS };
export default PostActivationKit;
